import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { CreditCard, ArrowUpRight, Check, AlertTriangle, RefreshCw } from "lucide-react";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

export default function AdminCardPayouts() {
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const [c, h] = await Promise.all([
        api.get("/admin/card-payouts/cards"),
        api.get("/admin/card-payouts/history"),
      ]);
      setCards(c.data); setHistory(h.data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  useEffect(() => { if (user?.is_admin) load(); }, [user?.is_admin]);

  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-card-payouts">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Admin</div>
        <h1 className="font-serif text-4xl mt-1">Cash out to my debit card</h1>
        <p className="text-moss mt-2 text-sm max-w-xl">
          Send money from your Stripe balance directly to your debit card. Instant payouts arrive within ~30 minutes;
          standard payouts land next business day.
        </p>

        {err && <div className="mt-4 text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="cash-out-error">{err}</div>}

        {!STRIPE_PK && (
          <div className="mt-6 bg-clay-light border border-clay/30 rounded-xl p-4 text-sm text-ink"
               data-testid="stripe-pk-missing">
            <div className="flex items-center gap-2 font-medium"><AlertTriangle className="w-4 h-4 text-clay" /> Stripe publishable key not configured</div>
            <p className="text-xs text-moss mt-1 leading-relaxed">
              Add <code className="bg-white px-1.5 py-0.5 rounded font-mono text-[10px]">REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_test_...</code> (or pk_live_ for real payouts) to
              <code className="bg-white px-1.5 py-0.5 rounded font-mono text-[10px]">/app/frontend/.env</code> and redeploy. Get it from Stripe Dashboard → Developers → API keys.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <div className="bg-linen border border-sand rounded-2xl p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Payout destination</div>
            <h3 className="font-serif text-xl mt-1">Attached debit cards</h3>
            {cards.length === 0 ? (
              <p className="text-xs text-sage mt-3">No cards yet — attach one below.</p>
            ) : (
              <ul className="mt-3 space-y-2" data-testid="attached-cards-list">
                {cards.map(c => (
                  <li key={c.id} className="bg-white border border-sand rounded-lg p-3 flex items-center gap-3">
                    <CreditCard className="w-4 h-4 text-forest" />
                    <div className="flex-1">
                      <div className="text-sm font-mono">{c.brand} •••• {c.last4}</div>
                      <div className="text-[10px] text-sage">exp {c.exp_month}/{c.exp_year}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {stripePromise && (
              <Elements stripe={stripePromise}>
                <AttachCardForm onAttached={load} />
              </Elements>
            )}
          </div>

          <div className="bg-linen border border-sand rounded-2xl p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Instant payout</div>
            <h3 className="font-serif text-xl mt-1">Send to my card</h3>
            <CreatePayoutForm cards={cards} onDone={load} />
          </div>
        </div>

        <div className="mt-10 bg-white border border-sand rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-sand flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Payout history</div>
            <button onClick={load} disabled={busy}
                    className="text-[10px] uppercase tracking-[0.18em] font-semibold text-sage hover:text-ink inline-flex items-center gap-1"
                    data-testid="payout-refresh-btn">
              <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
          <table className="w-full text-sm" data-testid="payout-history-table">
            <thead className="bg-cream-warm text-[10px] uppercase tracking-[0.2em] text-sage">
              <tr>
                <th className="text-left py-2.5 px-4">Date</th>
                <th className="text-left py-2.5 px-4">Amount</th>
                <th className="text-left py-2.5 px-4">Status</th>
                <th className="text-left py-2.5 px-4">Stripe payout ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sand-warm">
              {history.map(p => (
                <tr key={p.id}>
                  <td className="py-2 px-4 text-[11px] font-mono text-sage">
                    {p.created_at ? new Date(p.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="py-2 px-4 font-mono">${p.amount?.toFixed(2)}</td>
                  <td className="py-2 px-4">
                    <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${
                      p.status === "paid" ? "bg-mint text-forest-deep" :
                      p.status === "failed" ? "bg-clay-light text-clay" :
                      "bg-linen text-moss"
                    }`}>{p.status || "—"}</span>
                    {p.error && <div className="text-[10px] text-clay mt-1">{p.error}</div>}
                  </td>
                  <td className="py-2 px-4 font-mono text-[10px] text-sage truncate max-w-[220px]">{p.stripe_payout_id || "—"}</td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-sm text-sage">No payouts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AttachCardForm({ onAttached }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setErr("");
    const card = elements.getElement(CardElement);
    const { token, error } = await stripe.createToken(card, { currency: "usd" });
    if (error) { setErr(error.message); setBusy(false); return; }
    let pin = sessionStorage.getItem("admin_pin") || window.prompt("Admin PIN:") || "";
    if (!pin) { setBusy(false); return; }
    sessionStorage.setItem("admin_pin", pin);
    try {
      await api.post("/admin/card-payouts/attach-card", { token_id: token.id },
        { headers: { "X-Admin-PIN": pin } });
      card.clear();
      onAttached && onAttached();
    } catch (e) {
      if (e.response?.status === 401) sessionStorage.removeItem("admin_pin");
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="mt-4 space-y-3" data-testid="attach-card-form">
      <label className="block">
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Attach a new debit card</span>
        <div className="mt-1 bg-white border border-sand rounded-lg px-3 py-3">
          <CardElement options={{ hidePostalCode: false, style: { base: { fontSize: "14px", color: "#1A2520" } } }} />
        </div>
      </label>
      {err && <div className="text-xs text-clay" data-testid="attach-card-error">{err}</div>}
      <button type="submit" disabled={busy || !stripe}
              className="w-full bg-ink text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-ink-deep disabled:opacity-60"
              data-testid="attach-card-submit">
        {busy ? "Attaching…" : "Attach card"}
      </button>
    </form>
  );
}

function CreatePayoutForm({ cards, onDone }) {
  const [amount, setAmount] = useState("10");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setDone(null);
    let pin = sessionStorage.getItem("admin_pin") || window.prompt("Admin PIN:") || "";
    if (!pin) { setBusy(false); return; }
    sessionStorage.setItem("admin_pin", pin);
    try {
      const { data } = await api.post("/admin/card-payouts/create",
        { amount: parseFloat(amount) },
        { headers: { "X-Admin-PIN": pin } });
      setDone(data);
      onDone && onDone();
    } catch (e) {
      if (e.response?.status === 401) sessionStorage.removeItem("admin_pin");
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="mt-4 space-y-3" data-testid="create-payout-form">
      <label className="block">
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Amount (USD)</span>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-lg font-serif">$</span>
          <input type="number" min="1" step="0.01" required value={amount}
            onChange={e => setAmount(e.target.value)}
            className="w-full bg-white border border-sand rounded-lg px-4 py-2.5 font-mono"
            data-testid="payout-amount-input" />
        </div>
      </label>
      {err && <div className="text-xs text-clay" data-testid="payout-error">{err}</div>}
      {done && (
        <div className="text-xs text-forest bg-mint px-3 py-2 rounded" data-testid="payout-success">
          <Check className="w-3 h-3 inline mr-1" />
          Payout initiated · ${done.amount?.toFixed(2)} to {done.brand} •••• {done.card_last4} · status: {done.status}
        </div>
      )}
      <button type="submit" disabled={busy || cards.length === 0}
              className="w-full bg-forest text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-forest-deep disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
              data-testid="create-payout-submit">
        <ArrowUpRight className="w-4 h-4" />
        {busy ? "Sending…" : cards.length === 0 ? "Attach a card first" : "Send instant payout"}
      </button>
    </form>
  );
}
